import React from "react";
import { icon, number } from "../../../config/constants";

/**
 * Kendo DropDownList `itemRender`: shows a trailing arrow for custom privacy and delegates click for that row.
 * @param {function(React.MouseEvent): void} onCustomItemClick - Invoked when the custom item row is activated
 * @returns {(li: React.ReactElement, itemProps: { dataItem: { label: string, isCustom?: boolean } }) => React.ReactElement}
 * @author Abhishek Dhillon
 */
export const createPrivacyItemRender = (onCustomItemClick) => (li, itemProps) => {
  const isCustomItem = itemProps.dataItem.isCustom;

  const itemChildren = (
    <div className='d-flex align-items-center justify-content-between w-100'>
      <span>{itemProps.dataItem.label}</span>
      {isCustomItem && (
        <span
          className='privacy-custom-arrow d-flex align-items-center ml-2 font-size-fourteen'>
          {icon.ARROW_FORWARD_IOS}
        </span>
      )}
    </div>
  );

  return React.cloneElement(
    li,
    {
      ...li.props,
      style: {
        ...li.props.style,
        padding: "4px 8px",
      },
      onClick: isCustomItem ? onCustomItemClick : li.props.onClick,
    },
    itemChildren
  );
};

/**
 * Builds the display label for a privacy option (organisation / workspace / team suffixes).
 * @param {{ label: string, value: number }} item - Privacy dropdown item
 * @param {{ companyName?: string, queueName?: string, currentProjectName?: string }} [names] - Context for suffixes
 * @returns {string} Label with optional context in parentheses
 * @author Abhishek Dhillon
 */
export const getPrivacyOptionDisplayLabel = (item, names = {}) => {
  if (!item) return "";
  const { companyName, queueName, currentProjectName } = names;
  const { value, label: baseLabel } = item;

  if (value === number.ONE) {
    return `${baseLabel} (${companyName})`;
  }
  if (value === number.TWO) {
    return `${baseLabel} (${queueName || "Workspace"})`;
  }
  if (value === number.THREE) {
    return `${baseLabel} (${currentProjectName || baseLabel})`;
  }
  return baseLabel;
};

/**
 * Maps privacy dropdown items to display labels with org / workspace / team context.
 * @param {Array<{ label: string, value: number }>|undefined|null} privacyList - Raw privacy options
 * @param {{ companyName?: string, queueName?: string, currentProjectName?: string }} [names] - Context for label suffixes
 * @returns {Array|undefined|null} Transformed list with `isCustom` flags, or the original falsy input
 * @author Abhishek Dhillon
 */
export const getManipulatedPrivacyList = (privacyList, names = {}) => {
  if (!privacyList) return privacyList;

  return privacyList.map((item) => {
    const newLabel = getPrivacyOptionDisplayLabel(item, names);
    const isCustomItem = item.value === number.FOUR;
    return {
      ...item,
      label: newLabel,
      isCustom: isCustomItem,
    };
  });
};
